import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Typography } from '@mui/material'

import ConfirmDialog from '../../../../../components/ConfirmDialog/ConfirmDialog'
import { ConfirmDialogData } from '../../../../../types/ConfirmDialogData'
import { useGetDashboardPinnedGamesQuery, useGetFtueVideoCountsQuery } from '../../../../api/dashboard'
import { useGetInternalPinnedGamesQuery, useSaveInternalPinnedGamesMutation } from '../../../../api/games'
import { togglePinnedGameId } from '../../../games/helpers/gameListHelpers'
import { filterRowsByName, toGameRow } from '../../helpers/dashboardHelpers'
import { useDashboardAnalysts } from '../../hooks/useDashboardAnalysts'
import { DashboardAction, DashboardActionHandler, DashboardColumn, DashboardGameRow, DashboardListDialogProps } from '../../types/Dashboard'
import { DashboardGamesDialog } from '../DashboardGamesDialog/DashboardGamesDialog'
import { DashboardGamesTable } from '../DashboardGamesTable/DashboardGamesTable'

const columns = [
  DashboardColumn.Analyst,
  DashboardColumn.Icon,
  DashboardColumn.Name,
  DashboardColumn.Publisher,
  DashboardColumn.Subgenre,
  DashboardColumn.Powerscore,
  DashboardColumn.Ftue,
  DashboardColumn.AnalystOverview,
]
const actions = [DashboardAction.ViewAnalyses, DashboardAction.Edit, DashboardAction.VersionTags, DashboardAction.Unpin]

type PinnedGamesDialogProps = DashboardListDialogProps & {
  onMessage: (message: string, severity: 'success' | 'error') => void
}

const sameIds = (a: string[], b: string[]) => a.length === b.length && a.every((id, index) => id === b[index])

export const PinnedGamesDialog: FC<PinnedGamesDialogProps> = ({ open, marketIso, onClose, onAction, storedSubgenres, onMessage }) => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [confirmDialogData, setConfirmDialogData] = useState<ConfirmDialogData<DashboardGameRow>>()
  useEffect(() => setSearch(''), [open])

  const { analystsByAppId } = useDashboardAnalysts()
  const pinnedQuery = useGetInternalPinnedGamesQuery(undefined, { skip: !open, refetchOnMountOrArgChange: true })
  const pinnedIds = useMemo(() => pinnedQuery.data ?? [], [pinnedQuery.data])
  const gamesQuery = useGetDashboardPinnedGamesQuery({ ids: pinnedIds, marketIso }, { skip: !open || !pinnedIds.length })
  const ftueQuery = useGetFtueVideoCountsQuery(undefined, { skip: !open })
  const [savePinnedGames, { isLoading: isSaving }] = useSaveInternalPinnedGamesMutation()

  const rows = useMemo(
    () => (pinnedIds.length ? (gamesQuery.data ?? []).filter(({ id }) => pinnedIds.includes(id)).map((game) => toGameRow(game, marketIso)) : []),
    [gamesQuery.data, marketIso, pinnedIds]
  )
  const shownRows = useMemo(() => filterRowsByName(rows, search), [rows, search])
  // The whole list is sent, so an unread list would drop the other pins.
  const canUnpin = !!pinnedQuery.data && !pinnedQuery.isFetching && !isSaving

  const handleAction = useCallback<DashboardActionHandler>(
    (action, row) => {
      if (action !== DashboardAction.Unpin) return onAction(action, row)
      setConfirmDialogData({
        title: t('internal-analysts:games_unpin'),
        confirmText: t('internal-analysts:dashboard_unpin_confirm', { name: row.game.name || '---' }),
        actionText: t('internal-analysts:games_unpin'),
        data: row,
      })
    },
    [onAction, t]
  )

  const handleConfirm = async (confirmed?: ConfirmDialogData<DashboardGameRow>) => {
    setConfirmDialogData(undefined)
    const game = confirmed?.data?.game
    if (!game || !canUnpin) return
    const next = togglePinnedGameId(pinnedIds, game.id)
    try {
      const stored = await savePinnedGames(next).unwrap()
      if (!sameIds(stored, next)) return onMessage(t('internal-analysts:games_pin_not_stored'), 'error')
      onMessage(t('internal-analysts:games_unpinned', { name: game.name }), 'success')
    } catch {
      onMessage(t('internal-analysts:games_pin_failed'), 'error')
    }
  }

  const isEmpty = pinnedQuery.isSuccess && !pinnedIds.length

  return (
    <>
      <DashboardGamesDialog
        open={open}
        title={t(isEmpty ? 'internal-analysts:dashboard_pinned_none' : 'internal-analysts:dashboard_pinned_title')}
        onClose={onClose}
        search={search}
        onSearchChange={setSearch}
        isLoading={(pinnedQuery.isFetching && !pinnedQuery.data) || gamesQuery.isFetching}
        error={pinnedQuery.isError || gamesQuery.isError ? t('internal-analysts:dashboard_list_failed') : undefined}
        summary={ftueQuery.isError ? <Typography color="error.main">{t('internal-analysts:dashboard_ftue_failed')}</Typography> : undefined}
      >
        {!isEmpty && (
          <DashboardGamesTable
            rows={shownRows}
            columns={columns}
            actions={canUnpin ? actions : actions.filter((action) => action !== DashboardAction.Unpin)}
            onAction={handleAction}
            analystsByAppId={analystsByAppId}
            ftueCounts={ftueQuery.data}
            storedSubgenres={storedSubgenres}
            noRowsLabel={t('internal-analysts:dashboard_no_games')}
          />
        )}
      </DashboardGamesDialog>
      <ConfirmDialog open={!!confirmDialogData} confirmDialogData={confirmDialogData} onClose={handleConfirm} />
    </>
  )
}
